import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Slider } from '@flipster/react/design-system/slider';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { NumberInputWithSuffix } from '../../../input/number-input-with-suffix';
import { instrumentBunja } from '../../state/base';
import { tpslModalInputBunja } from '../state';
import { tpslModalValidationBunja } from '../validation';

export const SetQuantitySection = () => {
  const {
    tradeQtyPrecisionAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
    tpslAmountWarningMessageAtom,
    partialTpslSliderErrorMessageAtom,
    partialQtyAtom,
    partialQtyPercentAtom,
    partialQtyUsdtAtom,
    changeQtyPercent,
    changeQty,
  } = useBunja(setQuantitySectionBunja);
  const tradeQtyPrecision = useAtomValue(tradeQtyPrecisionAtom);
  const positionUnit = useAtomValue(positionUnitAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const quoteCurrencyPrecision = useAtomValue(quoteCurrencyPrecisionAtom);
  const tpslAmountWarningMessage = useAtomValue(tpslAmountWarningMessageAtom);
  const partialTpslSliderErrorMessage = useAtomValue(partialTpslSliderErrorMessageAtom);

  const partialQty = useAtomValue(partialQtyAtom);
  const partialQtyPercent = useAtomValue(partialQtyPercentAtom);
  const partialQtyUsdt = useAtomValue(partialQtyUsdtAtom);

  return (
    <section className={stack({ gap: 12 })}>
      <div className={stack({ gap: 8 })}>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
          <T id='Autogen_common.r251126.s_Set_quantity' />
        </p>
        <div
          className={hstack({
            w: '100%',
            h: 48,
            px: 12,
            gap: 8,
            textStyle: 'Text/L/Monospace/Regular',
            color: 'content.default.level-5',
            border: '1px solid',
            borderColor: 'border.default.level-2',
            rounded: 8,
            transition: 'border-color 200ms',
            _hover: {
              borderColor: 'border.default.level-4',
            },
            _focusWithin: {
              borderColor: 'border.default.level-4',
            },
          })}
        >
          <NumberInputWithSuffix
            precision={tradeQtyPrecision}
            value={partialQty}
            onChange={changeQty}
            suffix={
              <span translate='no' className={css({ textStyle: 'Text/L/Monospace/Regular' })}>
                {positionUnit}
              </span>
            }
          />
          <Formatted.Number
            prefix='≈ '
            value={String(partialQtyUsdt)}
            precision={quoteCurrencyPrecision}
            suffix={quoteCurrency}
            toFixed
            css={{
              ml: 'auto',
              textStyle: 'Text/L/Monospace/Regular',
              color: 'content.default.level-3',
              whiteSpace: 'nowrap',
            }}
          />
        </div>
      </div>
      <Slider
        value={partialQtyPercent}
        onChange={changeQtyPercent}
        min={0}
        max={100}
        step={1}
        marks={[0, 25, 50, 75, 100]}
        getLabel={(mark) => `${mark}%`}
      />
      <Explain.Error>{partialTpslSliderErrorMessage}</Explain.Error>
      <Explain.Warning>{tpslAmountWarningMessage}</Explain.Warning>
    </section>
  );
};

const setQuantitySectionBunja = bunja(() => {
  const { tradeQtyPrecisionAtom, positionUnitAtom, quoteCurrencyAtom, quoteCurrencyPrecisionAtom } =
    bunja.use(instrumentBunja);
  const { partialQtyAtom, partialQtyPercentAtom, partialQtyUsdtAtom, changeQtyPercent, changeQty } =
    bunja.use(tpslModalInputBunja);
  const { tpslAmountWarningMessageAtom, partialTpslSliderErrorMessageAtom } = bunja.use(tpslModalValidationBunja);

  return {
    tradeQtyPrecisionAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
    tpslAmountWarningMessageAtom,
    partialTpslSliderErrorMessageAtom,
    partialQtyAtom,
    partialQtyPercentAtom,
    partialQtyUsdtAtom,
    changeQtyPercent,
    changeQty,
  };
});
setQuantitySectionBunja.debugLabel = 'setQuantitySectionBunja';
